import type { BaseUser } from "@models";

export type TiptapVersionAuthor = {
  id: string | null;
  name: string;
};

export type TiptapVersionAuthorSource =
  | "missing"
  | "org-user"
  | "tracked-user"
  | "unknown-id";

export type ResolvedTiptapVersionAuthor = TiptapVersionAuthor & {
  source: TiptapVersionAuthorSource;
};

export type TiptapServerVersionMeta = {
  backendDocumentVersionId?: string;
  changesBy: string[];
  source?: string;
  triggeredBy?: string;
};

type TiptapVersionLike = {
  meta?: Record<string, unknown> | null;
};

export type TiptapVersionAuthorUser = Pick<
  BaseUser,
  "email" | "firstName" | "id" | "lastName"
>;

export const normalizeTiptapUserId = (value: string) =>
  value.trim().replace(/^#/, "");

export const getStringValue = (value: unknown) =>
  typeof value === "string" ? value.trim() : "";

export const getVersionUserDisplayName = (user: {
  email?: string | null;
  firstName?: string | null;
  id?: string | null;
  lastName?: string | null;
}) =>
  [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
  user.email ||
  user.id ||
  "Unknown user";

export const getTiptapVersionMeta = (
  version: TiptapVersionLike
): Record<string, unknown> => {
  const meta = version.meta;

  return meta && typeof meta === "object" ? meta : {};
};

export const getTiptapServerVersionMeta = (
  version: TiptapVersionLike
): TiptapServerVersionMeta => {
  const versionMeta = getTiptapVersionMeta(version);
  const serverMeta = versionMeta.__tiptap;
  const serverMetaRecord =
    serverMeta && typeof serverMeta === "object"
      ? (serverMeta as Record<string, unknown>)
      : {};
  const changesBy = Array.isArray(serverMetaRecord.changesBy)
    ? serverMetaRecord.changesBy
        .map(getStringValue)
        .filter(Boolean)
        .map(normalizeTiptapUserId)
    : [];
  const triggeredBy = getStringValue(serverMetaRecord.triggeredBy);
  const backendDocumentVersionId = getStringValue(
    serverMetaRecord.backendDocumentVersionId
  );
  const source =
    getStringValue(serverMetaRecord.source) ||
    getStringValue(versionMeta.source);

  return {
    ...(backendDocumentVersionId ? { backendDocumentVersionId } : {}),
    changesBy,
    ...(source ? { source } : {}),
    ...(triggeredBy ? { triggeredBy: normalizeTiptapUserId(triggeredBy) } : {}),
  };
};

export const getLegacyTiptapVersionAuthor = (
  version: TiptapVersionLike
): TiptapVersionAuthor | null => {
  const author = getTiptapVersionMeta(version).author;

  if (typeof author === "string") {
    return author.trim() ? { id: null, name: author.trim() } : null;
  }

  if (author && typeof author === "object") {
    const authorRecord = author as Record<string, unknown>;
    const name = getStringValue(authorRecord.name);
    const id = getStringValue(authorRecord.id);

    if (name || id) {
      return {
        id: id ? normalizeTiptapUserId(id) : null,
        name: name || `User ${normalizeTiptapUserId(id).slice(0, 8)}`,
      };
    }
  }

  return null;
};

export const resolveTiptapUserAuthor = ({
  missingName = "Unknown user",
  trackedChangesUserId,
  trackedChangesUserName,
  userId,
  usersById,
}: {
  missingName?: string;
  trackedChangesUserId?: string | null;
  trackedChangesUserName?: string | null;
  userId?: string | null;
  usersById: ReadonlyMap<string, TiptapVersionAuthorUser>;
}): ResolvedTiptapVersionAuthor => {
  const id = userId ? normalizeTiptapUserId(userId) : "";

  if (!id) {
    return { id: null, name: missingName, source: "missing" };
  }

  const user = usersById.get(id);

  if (user) {
    return {
      id,
      name: getVersionUserDisplayName(user),
      source: "org-user",
    };
  }

  const trackedId = trackedChangesUserId
    ? normalizeTiptapUserId(trackedChangesUserId)
    : "";
  const trackedName = trackedChangesUserName?.trim();

  if (id === trackedId && trackedName) {
    return {
      id,
      name: trackedName,
      source: "tracked-user",
    };
  }

  return {
    id,
    name: `User ${id.slice(0, 8)}`,
    source: "unknown-id",
  };
};

export const resolveTiptapVersionCardEditors = ({
  legacyAuthor: mappedLegacyAuthor,
  usersById,
  version,
}: {
  legacyAuthor?: TiptapVersionAuthor | null;
  usersById: ReadonlyMap<string, TiptapVersionAuthorUser>;
  version: TiptapVersionLike;
}): TiptapVersionAuthor[] => {
  const serverMeta = getTiptapServerVersionMeta(version);
  const authorIds = Array.from(
    new Set([
      ...serverMeta.changesBy,
      ...(serverMeta.triggeredBy ? [serverMeta.triggeredBy] : []),
    ])
  );

  if (
    authorIds.length === 0 &&
    !("__tiptap" in getTiptapVersionMeta(version))
  ) {
    const legacyAuthor =
      getLegacyTiptapVersionAuthor(version) ?? mappedLegacyAuthor;

    return legacyAuthor ? [legacyAuthor] : [];
  }

  return authorIds.map((authorId) =>
    resolveTiptapUserAuthor({
      userId: authorId,
      usersById,
    })
  );
};
